import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { trailToday } from 'utils/trailTime';
import { MenuItem, TextField, Typography } from '@mui/material';
import ContactChoice from 'components/TrailSafe/ContactChoice/ContactChoice';
import { ROUTES } from 'constants/RoutePaths';
import { buildRoutePath } from 'utils/utility';
import { useGetUserDetailsQuery } from 'app/api/usersApiSlice';
import { useCreatePlanMutation } from 'app/api/planApiSlice';
import TSButton from 'components/common/Button/Button';
import MLModal from 'components/common/Modal/Modal';
import MLToast from 'components/common/Toast/Toast';
import EmergencyContact from 'pages/Protected/User/EmergencyContact/EmergencyContact';

// "Plan a walk" on a Trail: one of its options, a day and start time (the
// Trail's local time, which is the walker's in Tasmania) and an Emergency
// Contact. Saving opens the new Plan, to work through its Gear List.
const NewPlan = ({ trail, toggleModal }) => {
  const navigate = useNavigate();
  const { showToast } = MLToast();
  const userEmail = useSelector((state) => state.global?.userEmail);
  const { data: userData, isLoading: isUserLoading } = useGetUserDetailsQuery(
    userEmail,
    { skip: !userEmail }
  );
  const [createPlan, { isLoading: isSaving }] = useCreatePlanMutation();

  const options = trail?.trailoptions ?? [];
  const today = trailToday();
  const [optionId, setOptionId] = useState(
    trail?.trailoptionid ?? options[0]?.TrailOptionID ?? ''
  );
  const [plannedDate, setPlannedDate] = useState(today);
  const [plannedStartTime, setPlannedStartTime] = useState('08:00');
  const [contactId, setContactId] = useState();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  if (isUserLoading) return <div>Loading...</div>;

  const contacts = [...(userData?.[0]?.emergency_contacts ?? [])].sort((a, b) =>
    a.ContactName.localeCompare(b.ContactName)
  );
  const selectedContactId =
    contactId ??
    contacts.find((contact) => contact.IsPrimary)?.EmergencyContactID;
  const isComplete =
    optionId && plannedDate && plannedStartTime && selectedContactId;

  const handleSave = async () => {
    const res = await createPlan({
      trail_option_id: optionId,
      emergency_contact_id: selectedContactId,
      planned_date: plannedDate,
      planned_start_time: plannedStartTime,
    });
    if (res.data) {
      showToast('Walk planned', 'success');
      toggleModal(false);
      navigate(
        `/${buildRoutePath(ROUTES.PLAN_DETAIL, { planId: res.data.plan_id })}`
      );
    } else {
      showToast(res.error?.data?.message || 'Something went wrong!', 'error');
    }
  };

  return (
    <div>
      <Typography variant="h2">Plan a walk</Typography>
      <Typography variant="body1" color="text.secondary" className="pt-1">
        {trail?.trailname}, {trail?.parkname}
      </Typography>
      <div className="flex flex-col gap-5 my-6">
        <TextField
          select
          label="Route"
          value={optionId}
          onChange={(e) => setOptionId(e.target.value)}
        >
          {options.map((option) => (
            <MenuItem key={option.TrailOptionID} value={option.TrailOptionID}>
              {option.CheckinPointLocation}: {option.TrailOptionName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          label="Date"
          value={plannedDate}
          onChange={(e) => setPlannedDate(e.target.value)}
          inputProps={{ min: today }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="time"
          label="Planned start time"
          value={plannedStartTime}
          onChange={(e) => setPlannedStartTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="h4" component="h3" className="pt-2">
          Emergency Contact
        </Typography>
        <Typography variant="body1">
          Who should we contact if you don't return? You can start your walk
          early or late on the day; we'll expect you back {trail?.formattedtime}{' '}
          after you actually start.
        </Typography>
        <ContactChoice
          contacts={contacts}
          value={selectedContactId}
          onChange={setContactId}
          onAdd={() => setIsContactModalOpen(true)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <TSButton
          btnText={
            isSaving
              ? 'Saving...'
              : !selectedContactId
                ? 'Select a contact to plan'
                : 'Save Plan'
          }
          btnDisable={!isComplete || isSaving}
          onClickFunc={handleSave}
        />
        <TSButton
          btnText="Cancel"
          btnVariant="outlined"
          onClickFunc={() => toggleModal(false)}
        />
      </div>
      <MLModal open={isContactModalOpen} setOpenModal={setIsContactModalOpen}>
        <EmergencyContact
          contact={null}
          index={99}
          isModal={true}
          toggleModal={setIsContactModalOpen}
        />
      </MLModal>
    </div>
  );
};

export default NewPlan;
